/**
 * Formats a number using accounting conventions.
 * Negative numbers are displayed in parentheses.
 * Defaults to 2 decimal places, or rounds to a specified number of decimal places.
 * Commas are added as thousands separators to the integer part only.
 *
 * Examples:
 * - formatAccountingNumber(1234.567) -> "1,234.57"
 * - formatAccountingNumber(-1234.567) -> "(1,234.57)"
 * - formatAccountingNumber(1234.567, 3) -> "1,234.567"
 * - formatAccountingNumber(-1234.567, 3) -> "(1,234.567)"
 * - formatAccountingNumber(1234) -> "1,234.00"
 * - formatAccountingNumber(-1234) -> "(1,234.00)"
 *
 * @param value - The number to format (can be null/undefined)
 * @param decimalPlaces - Optional number of decimal places (defaults to 2)
 * @returns The formatted number string, or undefined for invalid inputs
 */
export default function formatAccountingNumber(
  value: number | null | undefined,
  decimalPlaces?: number,
): string | undefined {
  if (value === null || value === undefined || isNaN(value)) return undefined;

  const absValue = Math.abs(value);

  // Format with specified decimal places or default to 2
  const formatted =
    decimalPlaces !== undefined
      ? absValue.toFixed(decimalPlaces)
      : absValue.toFixed(2);

  // Add commas as thousands separators (only to integer part)
  const [integerPart, decimalPart] = formatted.split(".");
  const integerWithCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const withCommas =
    decimalPart !== undefined
      ? `${integerWithCommas}.${decimalPart}`
      : integerWithCommas;

  return value < 0 ? `(${withCommas})` : withCommas;
}
